import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../context/NotificationContext';
import { notificationModel } from '../../data/notificationModel';
import { useTheme } from '../../context/ThemeContext';
import NotificationList from './NotificationList';

const NotificationBell = () => {
  const { t } = useTranslation();
  const { unreadCount, notifications } = useNotification();
  const { currentTheme } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // Calculate unread count from notifications array (more reliable)
  const calculatedUnreadCount = notifications.filter(n => !n.read).length;
  const displayUnreadCount = calculatedUnreadCount > 0 ? calculatedUnreadCount : unreadCount;

  // Notify parent when dropdown state changes (to hide hamburger menu)
  useEffect(() => {
    if (showDropdown) {
      window.dispatchEvent(new CustomEvent('notificationDropdownOpen'));
    } else {
      window.dispatchEvent(new CustomEvent('notificationDropdownClose'));
    }
  }, [showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!dropdownRef.current) return;
      
      // Don't close if clicking inside dropdown
      if (dropdownRef.current.contains(event.target)) {
        // Check if clicking on mark as read button - don't close
        const isMarkAsReadButton = event.target.closest('button')?.textContent?.includes('ทำเครื่องหมายว่าอ่านแล้วทั้งหมด') ||
                                   event.target.closest('button')?.textContent?.includes('Mark all as read');
        if (isMarkAsReadButton) {
          return; // Don't close when clicking mark as read
        }
        return; // Don't close when clicking inside dropdown
      }
      
      // Close if clicking outside
      setShowDropdown(false);
    };

    if (showDropdown) {
      // Use a slight delay to allow mark as read to complete
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 200);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="relative p-2 sm:p-2.5 text-white hover:bg-white/20 rounded-lg transition"
          aria-label={t('notifications.title')}
        >
          <svg
            className="w-5 h-5 sm:w-6 sm:h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
          {displayUnreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 block h-5 w-5 sm:h-6 sm:w-6 rounded-full bg-red-500 text-white text-xs sm:text-xs font-bold flex items-center justify-center border-2 border-white shadow-md">
              {displayUnreadCount > 9 ? '9+' : displayUnreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Mobile: Full screen overlay */}
      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setShowDropdown(false)}
          />
          
          {/* Mobile Dropdown */}
          <div className="fixed inset-0 z-50 md:hidden flex flex-col bg-white">
            <div 
              className="p-4 border-b border-gray-200 flex items-center justify-between text-white"
              style={{
                background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`
              }}
            >
              <h3 className="text-lg font-semibold">{t('notifications.title')}</h3>
              <div className="flex items-center gap-3">
                {displayUnreadCount > 0 && (
                  <span className="text-sm bg-red-500 px-2 py-1 rounded-full">
                    {displayUnreadCount} {t('notifications.new')}
                  </span>
                )}
                <button
                  onClick={() => setShowDropdown(false)}
                  className="text-white hover:text-white/80 transition-colors p-1"
                  aria-label={t('common.close')}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              <NotificationList onClose={() => setShowDropdown(false)} />
            </div>
          </div>

          {/* Desktop: Dropdown menu */}
          <div className="hidden md:block">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40 bg-transparent"
              onClick={(e) => {
                // Only close if clicking directly on backdrop
                if (e.target === e.currentTarget) {
                  setShowDropdown(false);
                }
              }}
            />
            {/* Dropdown Panel */}
            <div 
              className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[80vh] overflow-hidden flex flex-col"
              style={{ position: 'absolute' }}
              onMouseDown={(e) => {
                // Only stop propagation if not clicking on interactive elements
                const target = e.target;
                const isButton = target.closest('button');
                const isClickable = target.closest('[role="button"]') || target.closest('a');
                
                // Don't stop propagation for buttons - let them handle their own clicks
                if (!isButton && !isClickable) {
                  e.stopPropagation();
                }
              }}
            >
              <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-white sticky top-0 z-10">
                <h3 className="text-lg font-semibold text-gray-800">การแจ้งเตือน</h3>
                <div className="flex items-center gap-3">
                  {displayUnreadCount > 0 && (
                    <span className="text-sm bg-red-500 text-white px-2 py-1 rounded-full">
                      {displayUnreadCount} {t('notifications.new')}
                    </span>
                  )}
                  <button
                    onClick={() => setShowDropdown(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={t('common.close')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto flex-1">
                <NotificationList onClose={() => setShowDropdown(false)} />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default NotificationBell;


